import { useId, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  REFERENCE_GROUP_NAME_MAX,
  referenceGroupKey,
  type ReferenceGroupView,
} from '@scoutaround/script-breakdown-shared';

import { api, ApiError } from '../../lib/api.js';
import { Icon } from '../Icon.js';
import { MoreMenu } from '../ui/MoreMenu.js';
import { errorText, newRequestKey, useInvalidateReferences } from './hooks.js';

/**
 * GROUPS / THEMES: All references, then the production's own groups with how
 * many references each holds (unique live references, counted by the server).
 *
 * A group is a way of looking at the library, never a container that owns
 * what is in it: deleting one takes away the group and its memberships, and
 * every reference stays — the confirmation says so before anything happens.
 */
export function GroupsPanel({
  projectId,
  groups,
  total,
  selected,
  onSelect,
  canEdit,
  creating,
  onCreatingChange,
  testId = 'ref-groups',
}: {
  projectId: string;
  groups: readonly ReferenceGroupView[] | null;
  total: number | null;
  selected: string | null;
  onSelect: (groupId: string | null) => void;
  canEdit: boolean;
  creating: boolean;
  onCreatingChange: (creating: boolean) => void;
  testId?: string;
}) {
  const invalidate = useInvalidateReferences(projectId);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<ReferenceGroupView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const headingId = useId();

  const remove = useMutation({
    mutationFn: (group: ReferenceGroupView) => api.deleteReferenceGroup(projectId, group.id),
    onSuccess: async (_answer, group) => {
      setDeleting(null);
      if (selected === group.id) onSelect(null);
      await invalidate();
    },
    onError: (e) => {
      setError(errorText(e, 'The group could not be deleted.'));
    },
  });

  const list = groups ?? [];

  return (
    <div className="ref-groups-panel" data-testid={testId}>
      <div className="ref-groups-head">
        <h3 id={headingId} className="ref-groups-title">
          Groups / Themes
        </h3>
        {canEdit ? (
          <button
            type="button"
            className="ctl ref-icon-btn"
            aria-label="Create a group"
            title="Create a group"
            data-testid={`${testId}-create`}
            onClick={() => {
              setError(null);
              onCreatingChange(true);
            }}
          >
            <Icon name="plus" size={16} />
          </button>
        ) : null}
      </div>

      <ul className="ref-group-list" aria-labelledby={headingId}>
        <li>
          <button
            type="button"
            className="ref-group-row"
            aria-current={selected === null ? 'true' : undefined}
            data-testid={`${testId}-all`}
            onClick={() => {
              onSelect(null);
            }}
          >
            <Icon name="grid" size={15} />
            <span className="ref-group-name">All references</span>
            {total !== null ? <span className="ref-group-count">{total}</span> : null}
          </button>
        </li>
        {list.map((g) =>
          renaming === g.id ? (
            <li key={g.id}>
              <GroupNameForm
                projectId={projectId}
                groups={list}
                group={g}
                testId={`${testId}-rename-${g.id}`}
                onDone={() => {
                  setRenaming(null);
                }}
              />
            </li>
          ) : (
            <li key={g.id} className="ref-group-item">
              <button
                type="button"
                className="ref-group-row"
                aria-current={selected === g.id ? 'true' : undefined}
                data-testid={`${testId}-group-${g.id}`}
                onClick={() => {
                  onSelect(g.id);
                }}
              >
                <Icon name="folder" size={15} />
                <span className="ref-group-name">{g.name}</span>
                <span className="ref-group-count" data-testid={`${testId}-count-${g.id}`}>
                  {g.count}
                </span>
              </button>
              {canEdit ? (
                <MoreMenu
                  label={`Actions for the group ${g.name}`}
                  testId={`${testId}-menu-${g.id}`}
                  items={[
                    {
                      id: 'rename',
                      label: 'Rename',
                      testId: `${testId}-menu-${g.id}-rename`,
                      onSelect: () => {
                        setError(null);
                        setRenaming(g.id);
                      },
                    },
                    {
                      id: 'delete',
                      label: 'Delete group',
                      tone: 'danger',
                      testId: `${testId}-menu-${g.id}-delete`,
                      onSelect: () => {
                        setError(null);
                        setDeleting(g);
                      },
                    },
                  ]}
                />
              ) : null}
            </li>
          ),
        )}
      </ul>

      {creating ? (
        <GroupNameForm
          projectId={projectId}
          groups={list}
          group={null}
          testId={`${testId}-new`}
          onDone={() => {
            onCreatingChange(false);
          }}
        />
      ) : null}

      {groups !== null && list.length === 0 && !creating ? (
        <p className="ref-groups-hint" data-testid={`${testId}-hint`}>
          <Icon name="folder" size={16} />
          <span>Create groups to organize your references by mood, scene or production theme.</span>
        </p>
      ) : null}

      {deleting !== null ? (
        <div className="ref-confirm" role="alertdialog" aria-labelledby={`${testId}-delete-title`} data-testid={`${testId}-delete-confirm`}>
          <p id={`${testId}-delete-title`} className="ref-confirm-title">
            {`Delete the group “${deleting.name}”?`}
          </p>
          <p className="ref-confirm-body">
            {deleting.count === 0
              ? 'The group is empty. Deleting it removes nothing else.'
              : deleting.count === 1
                ? 'Only the group goes. The 1 reference in it stays in the library.'
                : `Only the group goes. The ${String(deleting.count)} references in it stay in the library.`}
          </p>
          <div className="ref-confirm-actions">
            <button
              type="button"
              className="ctl"
              data-testid={`${testId}-delete-cancel`}
              autoFocus
              onClick={() => {
                setDeleting(null);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn ref-danger-btn"
              disabled={remove.isPending}
              data-testid={`${testId}-delete-submit`}
              onClick={() => {
                remove.mutate(deleting);
              }}
            >
              {remove.isPending ? 'Deleting…' : 'Delete group'}
            </button>
          </div>
        </div>
      ) : null}

      {error !== null ? (
        <p className="ref-error" role="alert" data-testid={`${testId}-error`}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Name a new group, or rename one. One request key per opened form, so a retried create makes one group. */
function GroupNameForm({
  projectId,
  groups,
  group,
  testId,
  onDone,
}: {
  projectId: string;
  groups: readonly ReferenceGroupView[];
  group: ReferenceGroupView | null;
  testId: string;
  onDone: (name: string | null) => void;
}) {
  const invalidate = useInvalidateReferences(projectId);
  const [name, setName] = useState(group?.name ?? '');
  const [error, setError] = useState<string | null>(null);
  const requestKey = useRef(newRequestKey());
  const inputId = useId();

  const save = useMutation({
    mutationFn: (value: string) =>
      group === null
        ? api.createReferenceGroup(projectId, { clientRequestId: requestKey.current, name: value })
        : api.renameReferenceGroup(projectId, group.id, value),
    onSuccess: async (_answer, value) => {
      await invalidate();
      onDone(value);
    },
    onError: (e) => {
      setError(
        e instanceof ApiError && e.code === 'GROUP_NAME_TAKEN'
          ? 'There is already a group with that name.'
          : errorText(e, 'The group could not be saved.'),
      );
    },
  });

  const submit = () => {
    const value = name.trim();
    if (value === '') {
      setError('Give the group a name.');
      return;
    }
    if (group !== null && referenceGroupKey(value) === referenceGroupKey(group.name) && value === group.name) {
      onDone(null);
      return;
    }
    const clash = groups.find((g) => g.id !== group?.id && referenceGroupKey(g.name) === referenceGroupKey(value));
    if (clash) {
      setError(`There is already a group called “${clash.name}”.`);
      return;
    }
    setError(null);
    save.mutate(value);
  };

  return (
    <form
      className="ref-group-form"
      data-testid={testId}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          onDone(null);
        }
      }}
    >
      <label className="sr-only" htmlFor={inputId}>
        {group === null ? 'New group name' : `New name for ${group.name}`}
      </label>
      <input
        id={inputId}
        className="ref-input"
        value={name}
        autoFocus
        maxLength={REFERENCE_GROUP_NAME_MAX}
        placeholder="Group name"
        aria-invalid={error !== null}
        data-testid={`${testId}-input`}
        onChange={(event) => {
          setName(event.target.value);
          setError(null);
        }}
      />
      <div className="ref-group-form-actions">
        <button type="submit" className="ctl" data-on="1" disabled={save.isPending} data-testid={`${testId}-save`}>
          {save.isPending ? 'Saving…' : group === null ? 'Create' : 'Rename'}
        </button>
        <button
          type="button"
          className="ctl"
          data-testid={`${testId}-cancel`}
          onClick={() => {
            onDone(null);
          }}
        >
          Cancel
        </button>
      </div>
      {error !== null ? (
        <p className="ref-error" role="alert" data-testid={`${testId}-error`}>
          {error}
        </p>
      ) : null}
    </form>
  );
}
